import React, { useMemo, useState } from 'react';
import { Button, Card, Form, Checkbox, Space } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { getColorByIndex } from '../../../../utils/colors.helper';
import ComponentWithEditor from '../file-drawer/components/ComponentWithEditor'
import assignmentSelectors from '../../../../store/selectors/assignment.selectors'
import { FormContent } from './components/FormContent'
import './semantic-pattern-card.scss'

const SemanticPatternCard = ({ 
    semanticPattern, 
    onRemove, 
    criteriaThatAreWaitingForRegenerate, 
    setCriteriaFields, 
    onRegenerateAndProject,
    form,
    isSelected,
    onSelectChange
}) => {
    const language = useSelector(assignmentSelectors.language)
    const [focusedCriterion, setFocusedCriterion] = useState(null)
    
    // Watch for color changes in the form
    const selectedColor = Form.useWatch([`color-${semanticPattern.id}`], form)

    const patternsList = useMemo(() => {
        if (!semanticPattern?.projection?.length) return []
        
        return semanticPattern.projection.reduce((acc, p) => {
            if (p.matched_snippets) {
                p.matched_snippets.forEach(snippet => {
                    acc.push({
                        motifId: semanticPattern.id,
                        startRow: snippet.lineno,
                        endRow: snippet.end_lineno,
                        color: getColorByIndex(selectedColor ?? 0).value,
                        tooltip: `Hit by criterion: "${p.criterion}"`,
                        hideId: true,
                    })
                })
            }
            return acc
        }, [])
    }, [semanticPattern?.projection, selectedColor, semanticPattern.id])

    let selectedSnippets = []
    if (focusedCriterion && semanticPattern?.projection?.length) {
        const projection = semanticPattern.projection.find(p => p.criterion === focusedCriterion)
        selectedSnippets = projection?.matched_snippets?.map(snippet => ({
            start: snippet.lineno,
            end: snippet.end_lineno,
        })) || []
    }

    const criteriaWithProjection = useMemo(() => {
        if (!semanticPattern.criteria || !semanticPattern?.projection) {
            return semanticPattern.criteria
        }

        return semanticPattern.criteria.map(criterion => {
            const projection = semanticPattern.projection.find(p => p.criterion === criterion.value)
            if (projection) {
                return {
                    ...criterion,
                    reasoning: projection.reasoning,
                }
            }
            return criterion
        })
    }, [semanticPattern.criteria, semanticPattern?.projection])

    const handlePatternSelectChange = (e) => {
        onSelectChange(semanticPattern.id, e.target.checked);
    };

    return (
        <Card
            key={semanticPattern.id}
            title={
                <Space>
                    <Checkbox 
                        checked={isSelected} 
                        onChange={handlePatternSelectChange}
                        className="mr-8"
                    />
                    {semanticPattern.name}
                </Space>
            }
            className='mb-16 f-width'
            extra={
                <Button
                    type='text'
                    shape='circle'
                    aria-label='remove suggestion'
                    icon={<CloseOutlined />}
                    onClick={() => onRemove(semanticPattern.id)}
                />
            }
        >
            <ComponentWithEditor
                name={semanticPattern.file_name}
                content={semanticPattern.file_content}
                language={language}
                patternsList={patternsList}
                selectedSnippets={selectedSnippets}
                showEditor={!!semanticPattern.file_content}
            >
                <FormContent
                    semanticPattern={semanticPattern}
                    criteriaWithProjection={criteriaWithProjection}
                    criteriaThatAreWaitingForRegenerate={criteriaThatAreWaitingForRegenerate}
                    setCriteriaFields={setCriteriaFields}
                    onRegenerateAndProject={onRegenerateAndProject}
                    setFocusedCriterion={setFocusedCriterion}
                />
            </ComponentWithEditor>
        </Card>
    )
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(SemanticPatternCard, (prevProps, nextProps) => {
    // Only re-render if these props change
    return (
        prevProps.isSelected === nextProps.isSelected &&
        prevProps.semanticPattern === nextProps.semanticPattern &&
        prevProps.criteriaThatAreWaitingForRegenerate === nextProps.criteriaThatAreWaitingForRegenerate
    );
}); 